import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { IconPercent } from '../components/icons';
import { Chips, Empty, ErrorNote, FlashNote, Loading, Note, PageHead, Pill, RowCard, useFlash, VehicleTile } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime, percent, rupees, VEHICLE_LABEL } from '../lib/format';
import { useCommissions, useStaffNames } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import { VEHICLE_TYPES, type CommissionRule, type VehicleType } from '../lib/types';

const SOURCE: Record<string, string> = { TYPE: 'Its own rule', GLOBAL: 'The rule for every vehicle', DEFAULT: 'Default from settings' };
const forWhom = (t: VehicleType | null) => (t ? `${VEHICLE_LABEL[t]} only` : 'Every vehicle');

/** "2026-11-01T09:00" typed as India time → ISO. */
function istLocalToIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  const utc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) - 5.5 * 3600_000;
  return new Date(utc).toISOString();
}

export function CommissionPage() {
  const { isOwner } = useAuth();
  const q = useCommissions();
  const qc = useQueryClient();
  const staffName = useStaffNames();
  const [flash, show] = useFlash();
  const [busy, setBusy] = useState<string | null>(null);

  const remove = async (r: CommissionRule) => {
    setBusy(r.id);
    try {
      await api.del(`/admin/commissions/${r.id}`);
      show('Upcoming rule removed.');
      await qc.invalidateQueries({ queryKey: qk.commissions });
    } catch (e) {
      show(errorMessage(e), 'bad');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack">
      <PageHead
        title="Commission"
        sub="Zoomo Rides’ share of each fare. A ride keeps the rule that was in force when it was booked."
        actions={!isOwner && <Pill>View only</Pill>}
      />
      <FlashNote flash={flash} />
      {!isOwner && <Note>Only the owner can change commission. You can see every rule here.</Note>}
      {q.isLoading ? (
        <Loading rows={4} />
      ) : q.error || !q.data ? (
        <ErrorNote error={q.error} />
      ) : (
        <>
          <div className="grid-3">
            {VEHICLE_TYPES.map((t) => {
              const c = q.data.current.find((x) => x.vehicleType === t);
              const p = c?.percent ?? 0;
              return (
                <section key={t} className="card card-pad stack-sm" data-testid={`commission-${t}`}>
                  <div className="person">
                    <VehicleTile type={t} />
                    <div>
                      <div className="strong">{VEHICLE_LABEL[t]}</div>
                      <div className="small muted">{c ? SOURCE[c.source] : 'No rule'}</div>
                    </div>
                  </div>
                  <div className="big-percent">{percent(p)}</div>
                  <div className="small muted">
                    On a {rupees(10000)} fare: Zoomo Rides {rupees(Math.round(100 * p))}, driver {rupees(10000 - Math.round(100 * p))}.
                  </div>
                </section>
              );
            })}
          </div>

          {isOwner && <NewRule max={q.data.maxPercent} onDone={(text) => show(text)} />}

          <section className="card" aria-label="Upcoming rules">
            <div className="card-head">
              <h2>
                Starting later <span className="count-chip">{q.data.upcoming.length}</span>
              </h2>
            </div>
            {q.data.upcoming.length === 0 ? (
              <Empty title="Nothing scheduled" icon={IconPercent}>
                Rules set to start on a later date appear here until they begin.
              </Empty>
            ) : (
              <ul className="rows">
                {q.data.upcoming.map((r) => (
                  <li key={r.id}>
                    <div className="row-link" style={{ cursor: 'default' }}>
                      <RowCard
                        title={`${percent(r.percent)} · ${forWhom(r.vehicleType)}`}
                        sub={`Starts ${dateTime(r.effectiveFrom)}${r.note ? ` · ${r.note}` : ''}`}
                        side={
                          isOwner ? (
                            <button className="btn btn-sm btn-danger" disabled={busy === r.id} onClick={() => void remove(r)}>
                              Remove
                            </button>
                          ) : (
                            <Pill tone="warn">Scheduled</Pill>
                          )
                        }
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card" aria-label="All rules">
            <div className="card-head">
              <h2>Every rule</h2>
              <span className="small muted">Newest first · at most {percent(q.data.maxPercent)}</span>
            </div>
            <ul className="rows">
              {[...q.data.rules]
                .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))
                .map((r) => (
                  <li key={r.id}>
                    <div className="row-link" style={{ cursor: 'default' }}>
                      <RowCard
                        lead={r.vehicleType ? <VehicleTile type={r.vehicleType} small /> : <span className="veh-tile sm"><IconPercent /></span>}
                        title={`${percent(r.percent)} · ${forWhom(r.vehicleType)}`}
                        sub={`From ${dateTime(r.effectiveFrom)} · ${r.createdByAdminId ? staffName(r.createdByAdminId) : 'set at launch'}${r.note ? ` · ${r.note}` : ''}`}
                        side={r.inForce ? <Pill tone="good">In force</Pill> : new Date(r.effectiveFrom) > new Date() ? <Pill tone="warn">Later</Pill> : <Pill>Replaced</Pill>}
                      />
                    </div>
                  </li>
                ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

function NewRule({ max, onDone }: { max: number; onDone: (text: string) => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<'ALL' | VehicleType>('ALL');
  const [pct, setPct] = useState('');
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = Number(pct);
  const iso = when === 'later' ? istLocalToIso(at) : null;
  const problem =
    pct.trim() === '' || !Number.isFinite(n) || n < 0 || n > max
      ? `Between 0 and ${max}`
      : Math.round(n * 100) !== n * 100
        ? 'At most 2 decimals'
        : when === 'later' && (!iso || new Date(iso) <= new Date())
          ? 'Pick a date and time in the future'
          : null;

  return (
    <form
      className="card"
      aria-label="New commission rule"
      onSubmit={async (e) => {
        e.preventDefault();
        if (problem) return;
        setBusy(true);
        setError(null);
        try {
          await api.post('/admin/commissions', {
            vehicleType: type === 'ALL' ? null : type,
            percent: n,
            ...(iso ? { effectiveFrom: iso } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
          });
          await qc.invalidateQueries({ queryKey: qk.commissions });
          void qc.invalidateQueries({ queryKey: qk.settings });
          onDone(
            `${percent(n)} for ${type === 'ALL' ? 'every vehicle' : `${VEHICLE_LABEL[type].toLowerCase()}s`} ${iso ? `from ${dateTime(iso)}` : 'from now'}. Rides already booked keep their commission.`,
          );
          setPct('');
          setNote('');
          setAt('');
          setWhen('now');
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="card-head">
        <h2>New rule</h2>
      </div>
      <div className="card-body stack-sm" style={{ gap: 16 }}>
        <Chips<'ALL' | VehicleType>
          label="For"
          value={type}
          onChange={setType}
          options={[{ value: 'ALL', label: 'Every vehicle' }, ...VEHICLE_TYPES.map((t) => ({ value: t, label: VEHICLE_LABEL[t] }))]}
        />
        <div className="form-grid">
          <label className="field">
            <span className="field-label">Commission</span>
            <span className="input-affix">
              <input
                className="input num has-suffix"
                inputMode="decimal"
                value={pct}
                aria-label="Commission percent"
                aria-invalid={!!(pct && problem)}
                onChange={(e) => setPct(e.target.value.replace(/[^\d.]/g, '').slice(0, 5))}
              />
              <span className="suffix">%</span>
            </span>
            <span className={`field-hint${pct && problem ? ' bad' : ''}`}>{pct && problem ? problem : `0 to ${max}`}</span>
          </label>
          <div className="field">
            <span className="field-label">Starts</span>
            <Chips<'now' | 'later'>
              label="Starts"
              value={when}
              onChange={setWhen}
              options={[
                { value: 'now', label: 'Now' },
                { value: 'later', label: 'Later' },
              ]}
            />
          </div>
          {when === 'later' && (
            <label className="field">
              <span className="field-label">From (India time)</span>
              <input className="input" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
            </label>
          )}
          <label className="field">
            <span className="field-label">Note (optional)</span>
            <input className="input" maxLength={200} value={note} placeholder="For example: launch offer" onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-dark" disabled={busy || !!problem}>
            {busy ? 'Saving…' : 'Add rule'}
          </button>
        </div>
      </div>
    </form>
  );
}
